import clsx from 'clsx';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MenuItems } from 'src/app/AppRoutes';
import { ProLogo } from 'src/components/common/ProLogo';
import { CustomTabIcon } from 'src/components/CustomTabs/customTabIcons';
import { useCustomTabsContext } from 'src/components/CustomTabs/CustomTabsContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useOnClickOutside } from 'src/hooks/useClickOutside';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import {
  useUserHasCollapsedNavBarFeature,
  useUserHasFeature,
} from 'src/hooks/useUserHasFeature';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { CrossIcon, IconsFill } from 'src/svgs/Viagogo';
import { isInFullPageEventView } from 'src/utils/deepLinkUtils';
import { hasAccessToRoute } from 'src/utils/userUtils';
import { Feature } from 'src/WebApiController';

import { MainMenu } from './MainMenu/MainMenu';
import * as styles from './MainNavSideBar.css';
import { MenuItem } from './MenuItem/MenuItem';
import * as menuItemStyles from './MenuItem/MenuItem.css';
import { UserMenu } from './UserMenu/UserMenu';

type MainNavSideBarProps = {
  toggleMenu: (value?: boolean) => void;
  isExpanded: boolean;
};

const OPEN_MENU_MOUSE_EVENT = 'mouseenter';
const CLOSE_MENU_MOUSE_EVENT = 'mouseleave';

export const MainNavSideBar = ({
  toggleMenu,
  isExpanded,
}: MainNavSideBarProps) => {
  const isMobile = useMatchMedia('mobile');
  const { loginContext, appContext, mainRoute } = useAppContext();

  const delayedOpen = useMemo(() => {
    return debounce(() => {
      toggleMenu(true);
    }, 300);
  }, [toggleMenu]);

  const toggleClose = useCallback(() => {
    delayedOpen.cancel();
    toggleMenu(false);
  }, [delayedOpen, toggleMenu]);

  const mainSideBarRef = useRef<HTMLDivElement>(null);

  // Used to avoid the effect to unsubscribe and subscribe again to DOM events
  // when using a callback that depends on this.
  const useRefExpanded = useRef<boolean>(isExpanded);
  useRefExpanded.current = isExpanded;

  const hasHideNavAndFilterBarsFeature = useUserHasFeature(
    Feature.HideNavAndFilterBars
  );

  // POS-8591: thin/collapsed nav sidebar (feature 775); off = pre-collapse styling
  const collapsedNav = useUserHasCollapsedNavBarFeature();

  // POS-8591: on mobile (new nav) hold the sidebar mounted briefly so the
  // close animation can play before the parent unmounts it
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef<number>();

  const handleClose = useCallback(() => {
    if (isMobile && collapsedNav && isExpanded && !isClosing) {
      setIsClosing(true);
      closeTimeoutRef.current = window.setTimeout(() => {
        setIsClosing(false);
        toggleClose();
      }, 300);
      return;
    }

    toggleClose();
  }, [isMobile, collapsedNav, isExpanded, isClosing, toggleClose]);

  useEffect(() => () => window.clearTimeout(closeTimeoutRef.current), []);

  // Handle click outside to close on mobile
  const handleClickOutside = useCallback(() => {
    if (isMobile && isExpanded) {
      handleClose();
    }
  }, [isMobile, isExpanded, handleClose]);

  // Only enable click-outside when on mobile and menu is expanded
  useOnClickOutside(
    isMobile && isExpanded ? mainSideBarRef : { current: null },
    handleClickOutside
  );

  const handleOnMouseEnter = useCallback(
    (e: MouseEvent) => {
      const isExpanded = useRefExpanded.current;
      if (!isExpanded) {
        delayedOpen();
      }
    },
    [delayedOpen]
  );

  const handleOnMouseLeave = useCallback(
    (e: MouseEvent) => {
      // Only close the menu if the target event is fired
      // from the main parent div
      if (mainSideBarRef.current === e?.target) {
        toggleClose();
      }
    },
    [toggleClose]
  );

  useEffect(() => {
    const sideBar = mainSideBarRef.current;
    sideBar?.addEventListener(OPEN_MENU_MOUSE_EVENT, handleOnMouseEnter);
    return () => {
      sideBar?.removeEventListener(OPEN_MENU_MOUSE_EVENT, handleOnMouseEnter);
    };
  }, [handleOnMouseEnter]);

  useEffect(() => {
    const sideBar = mainSideBarRef.current;
    sideBar?.addEventListener(CLOSE_MENU_MOUSE_EVENT, handleOnMouseLeave);
    return () => {
      sideBar?.removeEventListener(CLOSE_MENU_MOUSE_EVENT, handleOnMouseLeave);
    };
  }, [handleOnMouseLeave]);

  if (hasHideNavAndFilterBarsFeature && isInFullPageEventView(mainRoute)) {
    return null;
  }

  return (
    <div
      ref={mainSideBarRef}
      className={clsx(styles.root, {
        [styles.rootLegacy]: !collapsedNav,
        [styles.expanded]: isExpanded,
        [styles.mobileRoot]: isMobile,
      })}
    >
      {isMobile && isExpanded && collapsedNav && (
        <div
          className={clsx(styles.scrim, {
            [styles.scrimClosing]: isClosing,
          })}
          onClick={handleClose}
        />
      )}
      <div
        className={clsx(styles.content, {
          [styles.contentMobile]: collapsedNav,
          [styles.contentMobileClosing]: isClosing,
        })}
      >
        {isMobile && (
          <div
            className={clsx(styles.closeButton, {
              [styles.closeButtonLegacy]: !collapsedNav,
            })}
            onClick={handleClose}
          >
            <CrossIcon />
          </div>
        )}
        <div
          className={clsx(styles.topMenuArea, {
            [styles.topMenuAreaLegacy]: !collapsedNav,
          })}
        >
          <div
            className={clsx(styles.logoContainer, {
              [styles.logoContainerLegacy]: !collapsedNav,
            })}
          >
            <ProLogo className={styles.logo} />
          </div>
          <MainMenu toggleClose={handleClose} collapsedNav={collapsedNav} />
          {hasAccessToRoute(
            loginContext?.user,
            appContext?.features,
            MainRoute.Email
          ) && (
            <MessagesItem
              toggleClose={handleClose}
              collapsedNav={collapsedNav}
            />
          )}
          {hasAccessToRoute(
            loginContext?.user,
            appContext?.features,
            MainRoute.ReachProSupport
          ) && (
            <ReachProSupportItem
              toggleClose={handleClose}
              collapsedNav={collapsedNav}
            />
          )}
          <CustomTopLevelTabs
            toggleClose={handleClose}
            collapsedNav={collapsedNav}
          />
        </div>
        {!collapsedNav && <hr className={styles.divider} />}
        <div
          className={clsx(styles.bottomMenuArea, {
            [styles.bottomMenuAreaLegacy]: !collapsedNav,
          })}
        >
          <UserMenu toggleClose={handleClose} collapsedNav={collapsedNav} />
        </div>
      </div>
    </div>
  );
};

const MessagesItem = ({
  toggleClose,
  collapsedNav,
}: {
  toggleClose: () => void;
  collapsedNav: boolean;
}) => {
  const { icon, collapsedNavIcon, label, path, view } =
    MenuItems[MainRoute.Email];

  const { mainRoute } = useAppContext();

  return (
    <MenuItem
      isActive={view === mainRoute}
      icon={collapsedNav ? (collapsedNavIcon ?? icon) : icon}
      textContent={label}
      path={path}
      onClick={toggleClose}
      collapsedNav={collapsedNav}
    />
  );
};

const ReachProSupportItem = ({
  toggleClose,
  collapsedNav,
}: {
  toggleClose: () => void;
  collapsedNav: boolean;
}) => {
  const { icon, collapsedNavIcon, label, path, view } =
    MenuItems[MainRoute.ReachProSupport];

  const { mainRoute } = useAppContext();

  return (
    <MenuItem
      isActive={view === mainRoute}
      icon={collapsedNav ? (collapsedNavIcon ?? icon) : icon}
      textContent={label}
      path={path}
      onClick={toggleClose}
      collapsedNav={collapsedNav}
    />
  );
};

const CustomTopLevelTabs = ({
  toggleClose,
  collapsedNav,
}: {
  toggleClose: () => void;
  collapsedNav: boolean;
}) => {
  const hasCustomTabsFeature = useUserHasFeature(Feature.CustomTabs);
  const { tabs } = useCustomTabsContext();
  const navigate = useNavigate();
  const topLevelTabs = tabs.filter((t) => t.scope === 'TopLevel');

  if (!hasCustomTabsFeature || topLevelTabs.length === 0) return null;

  return (
    <>
      {topLevelTabs.map((tab) => {
        const handleClick = () => {
          toggleClose();
          if (tab.tabType === 'Link' && tab.url) {
            const url = /^https?:\/\//i.test(tab.url)
              ? tab.url
              : `https://${tab.url}`;
            if (/^javascript:/i.test(url)) return;
            if (tab.openInNewTab !== false) {
              window.open(url, '_blank', 'noopener,noreferrer');
            } else {
              window.location.href = url;
            }
          } else {
            navigate(`/custom-tab/${tab.id}`);
          }
        };

        return (
          <div
            key={tab.id}
            className={menuItemStyles.menuItemContainer}
            onClick={handleClick}
            title={tab.description || tab.label}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClick();
            }}
          >
            <div
              className={clsx(menuItemStyles.menuItem, {
                [menuItemStyles.menuItemLegacy]: !collapsedNav,
              })}
            >
              <div
                className={clsx(menuItemStyles.icon, {
                  [menuItemStyles.iconLegacy]: !collapsedNav,
                })}
              >
                <CustomTabIcon
                  iconKey={tab.icon}
                  fill={IconsFill.currentColor}
                />
              </div>
              <span
                className={clsx(menuItemStyles.menuText, {
                  [menuItemStyles.menuTextLegacy]: !collapsedNav,
                })}
              >
                {tab.label}
              </span>
            </div>
          </div>
        );
      })}
    </>
  );
};
